import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { clearUserTypeCookie } from './authUserTypeCookie';

export type ApiResponse<T> = {
  data: T;
  status: number;
};

export type ApiRequestConfig = AxiosRequestConfig & {
  skipAuth?: boolean; // set to true to skip Authorization header
};

const API_BASE = process.env.REACT_APP_API_BASE;

const createHttpClient = (baseURL = API_BASE): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  instance.interceptors.request.use(
    (config: any) => {
      try {
        const skipAuth = config.skipAuth || false;
        delete config.skipAuth; // remove custom prop before sending

        const token = localStorage.getItem('token');
        if (token && config.headers && !skipAuth) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        // ignore localStorage errors in SSR scenarios
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // centralized error handling: map to normalized shape or rethrow
      if (error.response) {
        if (error.response.status === 401) {
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('token_date');
            clearUserTypeCookie();
          } catch (e) {
            // ignore localStorage errors in SSR scenarios
          }

          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            if (currentPath !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error.response);
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

const http = createHttpClient();

export async function get<T>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
  const res = await http.get<T>(url, config);
  return { data: res.data as T, status: res.status };
}

export async function post<T, B = unknown>(
  url: string,
  body?: B,
  config?: ApiRequestConfig,
): Promise<ApiResponse<T>> {
  const res = await http.post<T>(url, body, config);
  return { data: res.data as T, status: res.status };
}

export async function put<T, B = unknown>(
  url: string,
  body?: B,
  config?: ApiRequestConfig,
): Promise<ApiResponse<T>> {
  const res = await http.put<T>(url, body, config);
  return { data: res.data as T, status: res.status };
}

export async function del<T>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
  const res = await http.delete<T>(url, config);
  return { data: res.data as T, status: res.status };
}

export default http;
